/**
 * @description 周期奖励: 每日奖励、每周奖励、每月奖励
 */
import { RewardTypeEnum, RewardStatusEnum } from "@/types/reward";
import Image from "next/image";
import { RewardIcon } from "./reward-icon";
import { isEmpty } from "lodash-es";
import ProgressBar from "@/features/reward/components/progress-bar";
import GiftIcon from '@/features/reward/assets/gift.png';
import { Button } from "@/components/ui/button";
import { useBalanceFormatter } from "@/hooks/use-balance-formatter";
import { useRewardCountDown } from "@/features/reward/hooks/useRewardCountDown";
import { useTranslations } from "next-intl";
import cn from "@/utils/classnames";
import { TabTitle } from "@/store/base";
import { useClaimDebounce } from "./use-claim-debounce";

interface PeriodicRewardProps {
  type: RewardTypeEnum.DAILY_REWARD | RewardTypeEnum.WEEKLY_REWARD | RewardTypeEnum.MONTHLY_REWARD;
  status: RewardStatusEnum;
  wageredProgress?: {
    currentAmount: number;
    targetAmount: number;
  };
  countdown?: number; // 秒
  next_distributed_at?: number; // 秒
  currency: string;
  amount: number;
  userLevel: number;
  claimCurrency: string;
  claimAmount: number;
  bonusWagerMultiple?: number;
  calendarAmount?: number;
  onTimeOut?: () => void;
  onClaim: () => Promise<unknown>;
}


export default function PeriodicReward(props: PeriodicRewardProps) {
  const { type, status, next_distributed_at, wageredProgress, currency, amount, onTimeOut, onClaim } = props;
  const { formatCurrencyDetail } = useBalanceFormatter();
  const { loading, run } = useClaimDebounce(onClaim);

  const { countdownStr, isOverOneDay } = useRewardCountDown({ targetDate: Number(next_distributed_at) * 1000, onEnd: onTimeOut });
  const t = useTranslations('reward');
  const noReward = status === RewardStatusEnum.NO_REWARD;

  const LabelMap = {
    [RewardTypeEnum.DAILY_REWARD]: t('dailyReward'),
    [RewardTypeEnum.WEEKLY_REWARD]: t('weeklyReward'),
    [RewardTypeEnum.MONTHLY_REWARD]: t('monthlyReward'),
  };

  const showProgress = status === RewardStatusEnum.PROGRESS;
  const progressPercent = showProgress && !isEmpty(wageredProgress) ?
    (+wageredProgress.currentAmount / +wageredProgress.targetAmount) * 100 : 0;

  const handleClaim = async () => {
    return run();
  };

  return (
    <div className="bg-dark-special rounded-lg py-3 pr-3 flex items-center justify-between" style={{ background: 'linear-gradient(to bottom right, #1D3657 0%, #1B2131 65%, #1B2131 100%)', }}>
      <RewardIcon type={type} className="mr-2.5" />
      <div className="flex-1">
        <div className="text-white text-xs font-medium leading-3">{LabelMap[type]}</div>
        {
          showProgress && (
            <>
              <div className="flex items-center justify-between mt-3">
                <div className="text-tertiary text-[11px] font-medium leading-3">
                  {t('wager')} {`${formatCurrencyDetail((wageredProgress?.currentAmount || 0))} / ${formatCurrencyDetail((wageredProgress?.targetAmount || 0)) + ' ' + currency}`}
                </div>
                <Image src={GiftIcon.src} alt="gift" width={12} height={12} />
              </div>
              <ProgressBar progress={progressPercent} className="mt-2 h-2" barClassName="bg-accent-blue" />
            </>
          )
        }
        {
          !!amount ? (
            <div className="text-[#FFC600] text-xss font-medium leading-3 mt-3">
              {formatCurrencyDetail(amount)} {currency}
            </div>
          ) : noReward ? (
            <div className="text-tertiary font-medium text-[11px] mt-3 leading-4">
              {t.rich('playGameForBonus', { game1: 'Casino', game2: 'Rummy', game3: TabTitle.Fantasy, highlight: (chunks) => <span className="text-white">{chunks}</span> })}
            </div>
          ) : null
        }
      </div>
      {[RewardStatusEnum.COUNT_DOWN, RewardStatusEnum.CLAIM].includes(status) && (
        <Button
          disabled={status === RewardStatusEnum.COUNT_DOWN || loading}
          variant={status === RewardStatusEnum.CLAIM ? 'success' : 'cancel'}
          className={cn("h-8", status === RewardStatusEnum.COUNT_DOWN ? 'font-medium text-[13px] text-[#7A879A] px-[10px] bg-[rgba(142,210,255,0.04)] disabled:opacity-1' : 'w-[90px]')}
          onClick={status === RewardStatusEnum.CLAIM ? handleClaim : undefined}
          loading={loading && status === RewardStatusEnum.CLAIM}
        >{status === RewardStatusEnum.CLAIM ? t('claim') : <span className={`text-left ${isOverOneDay ? 'w-[71px]' : 'w-[70px] pl-[10px]'}`}>{countdownStr}</span>}</Button>
      )}
    </div>
  );
}
